import { useAuth } from '@clerk/clerk-react';
import { httpService, REGEX } from '@flexo/general';
import { useFormValidation, useMobileLogout } from '@flexo/hooks';
import { ALERT } from '@flexo/reducers';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Textfield from '../../textfield/textfield';
import styles from './../alert.module.scss';

const ENV = (import.meta as any).env;

interface AlertConfirmCredentialsMobileProps {
  type: 'credentials' | 'delete';
}

export default function AlertConfirmCredentialsMobile({
  type = 'credentials',
}: AlertConfirmCredentialsMobileProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const alertState = useSelector((state: any) => state.alert);
  const { getToken, signOut } = useAuth();
  const handleLogout = useMobileLogout({
    redirectToForgotPassword: true,
    clerkSignOut: signOut,
  });

  // Get the appropriate translation key based on type
  const translationKey =
    type === 'delete'
      ? 'banners.confirm_delete_account'
      : 'banners.confirm_credentials_changes';
  const bannerContent = t(translationKey, {
    returnObjects: true,
  }) as any;

  const formRef = useRef<any>(null);

  const [alertContent, setAlertContent] = useState<any>(
    t('alerts.wrong_request_data', { returnObjects: true })
  );
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [formError, setFormError] = useState<boolean>(false);
  const [fieldVisible, setFieldVisible] = useState<boolean>(false);
  const [sendingForm, setSendingForm] = useState<boolean>(false);
  const [showPasswordReset, setShowPasswordReset] = useState<boolean>(false);
  const [passwordField, setPasswordField] = useState<string>();
  const [icon, setIcon] = useState('visibility');

  const [inputType, setInputType] = useState({
    password: 'password',
    confirmPassword: 'password',
  });

  // Form validation hook
  const { errors, blur, handleBlur, handleFocus } = useFormValidation(
    {
      fields: ['password'],
      validationRules: {
        password: {
          validate: (value) => REGEX.password.test(value),
        },
      },
      hasPasswordFields: false, // No password conditions needed here
    },
    { password: passwordField || '' }
  );

  async function handleInputChange(event: any) {
    if (showAlert === true && formError === true) {
      setShowAlert(false);
      setShowPasswordReset(false);
    }
    setPasswordField(event.target.value);

    setFormError(false);
  }

  async function manageAlertCallBack() {
    let result: any = null;
    if (alertState?.alert?.callback) {
      result = await alertState.alert.callback(passwordField);

      dispatch(ALERT.ResetAlert({} as any));
    }

    if (typeof result === 'number' && result > 200) {
      setFormError(true);

      if (result === 422) {
        setAlertContent(
          t('alerts.wrong_request_data', { returnObjects: true })
        );
      }

      setShowAlert(true);
    } else if (result === null) {
      dispatch(ALERT.ResetAlert({} as any));
      dispatch(
        ALERT.SetAlert({
          value: {
            type: 'BANNER_UNEXPECTED_ERROR',
          },
        } as any)
      );
    }
  }

  const togglePasswordVisibility = (name: string) => {
    setInputType((prevState) => ({
      ...prevState,
      [name]: prevState[name] === 'password' ? 'text' : 'password',
    }));
    setIcon(icon === 'visibility' ? 'hide-visibility' : 'visibility');
  };

  async function checkPasswordValidity() {
    setSendingForm(true);

    try {
      // Get Clerk token for authenticated request
      const token = await getToken();

      if (!token || !passwordField) {
        setFormError(true);
        setSendingForm(false);
        return;
      }

      const url = `${
        ENV.VITE_APP_CUSTOM_BASE_URL || ENV.VITE_APP_BASE_URL
      }/common/auth/v1/verify-password`;

      // Verify password using backend Clerk verification endpoint
      const response = await httpService.post({
        url,
        data: { password: passwordField },
        idToken: token,
        skipLogoutOn401: true,
      });

      if (response?.data?.verified) {
        await manageAlertCallBack();
      } else {
        setShowAlert(false);
        setFormError(true);
        setShowPasswordReset(true);
      }
    } catch (error: any) {
      console.error('Password verification failed:', error);
      setShowAlert(false);
      setFormError(true);
      setShowPasswordReset(true);
    }

    setSendingForm(false);
    setPasswordField('');
  }

  const onCancel = () => {
    dispatch(ALERT.ResetAlert({} as any));
    setPasswordField('');
  };

  const onClickLogOut = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleLogout();
  };

  // Get the appropriate ID and CSS classes based on type
  const componentId =
    type === 'delete'
      ? 'alert-confirm-delete-account-mobile'
      : 'alert-confirm-credentials-changes-mobile';
  const formId =
    type === 'delete'
      ? 'alert-confirm-delete-account-mobile-form'
      : 'alert-confirm-credentials-changes-mobile-form';
  const confirmButtonClass =
    type === 'delete'
      ? 'Alert__Footer__Actions__Mobile__Button__Delete'
      : 'Alert__Footer__Actions__Mobile__Button__Confirm';

  return (
    <div>
      <div
        id={componentId}
        className={`${styles.Alert__Confirm__Credentials__Changes__Mobile} ${styles.Alert}`}
      >
        {sendingForm && (
          <div className={styles.Alert__Loading__Overlay}>
            <div className={styles.Alert__Loading__Pulse}></div>
          </div>
        )}
        <div className={styles.Alert__Title}>{bannerContent.title}</div>
        <div className={`paragraph ${styles.Alert__Content}`}>
          {bannerContent.description}
        </div>

        <form id={formId} className={styles.Alert__Content__Form}>
          <div className={styles.Alert__Content__Form__Input} ref={formRef}>
            <Textfield
              variant={
                ENV.VITE_APP_NAME === 'plenitude' ? 'containedLabel' : 'default'
              }
              name="password"
              label={bannerContent.inputLabel}
              type={inputType.password}
              value={passwordField}
              placeholder={bannerContent.inputPlaceholder}
              error={(errors.password && blur.password) || formError}
              onPaste={(e) => false}
              onCopy={(e) => false}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              autoComplete="current-password"
              onChange={(ev: any) => handleInputChange(ev)}
              onBlur={handleBlur}
              onFocus={handleFocus}
              icon={icon}
              onIconClick={togglePasswordVisibility}
              errorDescription={
                (errors.password && blur.password) || formError
                  ? 'Inserisci una password valida'
                  : undefined
              }
            />
          </div>
        </form>

        <div
          className={` paragraph ${styles.Alert__Content}`}
          style={{ marginBottom: '20px' }}
        >
          <span className={styles.Alert__Content__Link} onClick={onClickLogOut}>
            {bannerContent.forgotPassword}
          </span>
          <span> {bannerContent.forgotPasswordLabel}</span>
        </div>

        <div className={styles.Alert__Footer__Actions__Mobile}>
          <button
            className={`${styles.Alert__Footer__Actions__Mobile__Button} ${styles.Alert__Footer__Actions__Mobile__Button__Dismiss}`}
            onClick={() => onCancel()}
          >
            {bannerContent.dismiss}
          </button>

          <button
            className={`${styles.Alert__Footer__Actions__Mobile__Button} ${styles[confirmButtonClass]}`}
            onClick={() => {
              checkPasswordValidity();
            }}
            disabled={sendingForm || errors.password || !passwordField}
          >
            {bannerContent.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}

export { AlertConfirmCredentialsMobile };
